import { useMemo, useState } from 'react'
import { transicaoDeMembros, temTransicaoNativa } from '../transicaoDeMembros'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CharacterList } from './CharacterList'
import { CharacterSheetEditor } from './CharacterSheetEditor'
import { FichaDoPersonagem } from './FichaDoPersonagem'
import { SorteioPendentes } from './SorteioPendentes'
import { Logo } from './Logo'
import { X, Search, ChevronLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { apiService } from '@/services/api'
import type { Character } from '@/types'
import type { Member as MemberType } from '@/types/member'
import type { UnreadCounts } from '@/hooks/useUnreadCounts'

interface SidePanelProps {
  characters: Character[];
  members: MemberType[];
  tableId?: string; // ID da mesa para buscar game_config dos atributos
  onCharacterChatClick?: (character: Character) => void;
  currentUserId?: string;
  unreadCounts?: UnreadCounts; // Novo: contagem de mensagens não lidas
  onClose?: () => void;
  showHeader?: boolean;
  tableName?: string;
  userFullName?: string;
  userRole?: string;
  isUserOnline?: (userId: string) => boolean; // Função para verificar status online
  // Criação mediada (fase 3a): seção "Aguardando sorteio"
  sorteioMediada?: boolean;
  /** Modulo "dice" ativo na mesa. Sem ele nao ha o que testar rolando:
   *  o "Pedir teste de atributo" da ficha some junto com os dados
   *  (docs/PLANO_MODULO_DADOS.md). */
  dadosAtivos?: boolean;
  onSortear?: (userId: string) => void; // presente só para o mestre
  sorteandoUserId?: string | null;
  /**
   * Como a ficha aparece a partir desta lista
   * (docs/PLANO_MEMBROS_E_FICHA.md).
   *
   * - `sobreposta` (padrão): `Drawer` por cima. É o desktop, onde há espaço
   *   para a lista e a ficha ao mesmo tempo.
   * - `empilhada`: a ficha ocupa o lugar da lista, com "‹ Membros" para
   *   voltar. É o celular, onde só cabe uma tela.
   *
   * É prop, e não `useIsMobile` lido aqui dentro, de propósito: o SidePanel
   * serve dois donos e não deve adivinhar onde está. Quem monta decide.
   */
  fichaComo?: 'sobreposta' | 'empilhada';
}

export default function SidePanel({
  characters,
  members,
  tableId,
  onCharacterChatClick,
  currentUserId,
  unreadCounts,
  onClose,
  showHeader = true,
  tableName,
  userFullName,
  userRole,
  isUserOnline,
  sorteioMediada = false,
  dadosAtivos = false,
  onSortear,
  sorteandoUserId,
  fichaComo = 'sobreposta'
}: SidePanelProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [isUpdatingStats, setIsUpdatingStats] = useState(false)

  // A ficha aberta guarda o ID, não o objeto (2026-07-31).
  //
  // Guardando o objeto, ele virava uma FOTOGRAFIA do instante do clique: o
  // `characters` chegava atualizado pelo WebSocket e a ficha aberta continuava
  // mostrando o HP velho até alguém fechar e reabrir. No celular a ficha ocupa
  // a tela inteira, então era a única coisa que o jogador estava vendo — e
  // estava desatualizada. Pelo ID, ela acompanha a mesa ao vivo.
  //
  // Some da lista (personagem apagado) => `undefined` => a ficha fecha sozinha,
  // que é o certo: não há mais o que mostrar.
  const [editingCharacterId, setEditingCharacterId] = useState<string | null>(null)
  const editingCharacter = useMemo(
    () => characters.find(c => c.id === editingCharacterId) ?? null,
    [characters, editingCharacterId]
  )

  // Verificar se é mestre
  const isMaster = userRole?.toLowerCase() === 'mestre' || userRole?.toLowerCase() === 'master'

  // Mestre da mesa (presença exibida no topo da lista)
  const mestreDaMesa = useMemo(
    () => members.find(m => m.role === 'mestre' && m.isActive !== false),
    [members]
  )

  // Criação mediada: jogadores (papel jogador, ativos) ainda sem personagem
  const sorteioPendentes = useMemo(() => {
    if (!sorteioMediada) return []
    return members
      .filter(m => m.role === 'jogador' && m.isActive !== false)
      .filter(m => !characters.some(c => c.userId === m.userId && !c.isNpc))
      .map(m => ({ userId: m.userId, name: m.userFullName }))
  }, [sorteioMediada, members, characters])

  const normalizedSearch = searchTerm.trim().toLowerCase()
  
  // Criar mapa de membros por userId para identificar tipo do personagem
  const membersByUserId = useMemo(() => {
    const map = new Map<string, MemberType>()
    members.forEach(member => {
      map.set(member.userId, member)
    })
    return map
  }, [members])

  // Filtrar personagens
  const filteredCharacters = useMemo(() => {
    if (!normalizedSearch) return characters
    return characters.filter((character) =>
      character.name.toLowerCase().includes(normalizedSearch) ||
      character.className.toLowerCase().includes(normalizedSearch)
    )
  }, [characters, normalizedSearch])
  
  // Handler para salvar stats (incluindo atributos dinâmicos)
  const handleSaveStats = async (characterId: string, updates: {
    hp_current?: number
    hp_total?: number
    points?: number
    level?: number
    attributes?: Record<string, number>
    name?: string
    class_name?: string
  }) => {
    setIsUpdatingStats(true)
    try {
      await apiService.updateCharacterStats(characterId, updates)
      // WebSocket broadcast vai atualizar automaticamente
    } catch (error) {
      console.error('Erro ao atualizar stats:', error)
      throw error
    } finally {
      setIsUpdatingStats(false)
    }
  }

  // Regras da ficha, iguais nos dois modos de apresentação.
  const podePedirTeste =
    dadosAtivos && isMaster && !editingCharacter?.isNpc &&
    editingCharacter?.userId !== currentUserId
  // Presença do dono da ficha, ao vivo: o pedido de teste precisa dele na
  // mesa. Sem a função de presença (ainda carregando), não bloqueia — o
  // servidor recusa de todo modo, e a UI nunca é a autoridade.
  const convocadoOnline =
    !editingCharacter?.userId ||
    (isUserOnline ? isUserOnline(editingCharacter.userId) : true)

  // A troca de tela com transição nativa quando o navegador sabe fazer —
  // o COMO mora em ../transicaoDeMembros; aqui só a decisão de QUANDO:
  // exclusivamente no modo empilhado (no desktop a coluna é fixa).
  const navegar = (direcao: 'avanca' | 'volta', mudanca: () => void) => {
    if (fichaComo !== 'empilhada') {
      mudanca()
      return
    }
    transicaoDeMembros(direcao, mudanca)
  }
    // O deslizamento que o drawer tinha e a aba perdeu (2026-07-31, pedido do
  // Paulo: "falta elegância na abertura e fechamento").
  //
  // A direção carrega significado, como em qualquer navegação hierárquica: a
  // lista entra pela ESQUERDA (é de onde o painel de membros vinha, e a barra
  // de baixo fica à esquerda do gesto mental de "voltar"); a ficha entra pela
  // DIREITA, porque é um nível abaixo.
  //
  // Só no modo empilhado: no desktop o SidePanel é coluna fixa e deslizar na
  // montagem da página seria movimento sem motivo.
  //
  // O `key` não é decoração — sem ele o React reaproveita a mesma <div> entre
  // os dois modos (mesmo tipo, mesma classe base), não há remontagem, e a
  // animação de entrada nunca dispara.
  const deslize = (de: 'esquerda' | 'direita') =>
    fichaComo === 'empilhada' && !temTransicaoNativa()
      ? cn(
          'animate-in fade-in ease-out duration-200 motion-reduce:animate-none',
          de === 'esquerda' ? 'slide-in-from-left-8' : 'slide-in-from-right-8'
        )
      : ''

  // O nome amarra o antes e o depois: com as duas raízes batizadas de
  // `painel-membros`, o navegador anima a troca DESTA região e deixa o resto
  // da página (barras de cima e de baixo, idênticas nos dois estados) quieto.
  const nomeDaTransicao = fichaComo === 'empilhada'
    ? ({ viewTransitionName: 'painel-membros' } as React.CSSProperties)
    : undefined

  // === MODO EMPILHADO: a ficha no lugar da lista ===
  //
  // No celular só cabe uma tela, e "Membros" é uma aba como as outras: a ficha
  // não entra por cima, ela substitui a lista. Voltar é o "‹ Membros" — a
  // saída é do invólucro, e aqui o invólucro é este bloco.
  if (fichaComo === 'empilhada' && editingCharacter && tableId) {
    return (
      <div
        key="ficha"
        style={nomeDaTransicao}
        className={cn(
          'h-full bg-game-secondary dark:bg-zinc-900/95 flex flex-col',
          deslize('direita')
        )}
      >
        <button
          type="button"
          onClick={() => navegar('volta', () => setEditingCharacterId(null))}
          data-testid="voltar-para-membros"
          className="flex w-full flex-shrink-0 items-center gap-1.5 border-b border-game-border-primary dark:border-zinc-800 bg-game-secondary px-4 py-3 text-sm font-semibold text-game-secondary dark:bg-zinc-900/95 dark:text-zinc-300 hover:text-game-primary dark:hover:text-white"
        >
          <ChevronLeft className="h-5 w-5" />
          Membros
        </button>

        <FichaDoPersonagem
          character={editingCharacter}
          tableId={tableId}
          open
          onOpenChange={(aberta) => { if (!aberta) navegar('volta', () => setEditingCharacterId(null)) }}
          onSave={handleSaveStats}
          isUpdating={isUpdatingStats}
          readOnly={!isMaster}
          podePedirTeste={podePedirTeste}
          convocadoOnline={convocadoOnline}
        />
      </div>
    )
  }

  return (
    <div
      key="lista"
      style={nomeDaTransicao}
      className={cn(
        'h-full bg-game-secondary dark:bg-zinc-900/95 flex flex-col',
        deslize('esquerda')
      )}
    >
      {/* Header - Informações da Mesa e botão fechar (mobile) */}
      {showHeader && (
        <div className="border-b border-game-border-primary dark:border-zinc-800 bg-game-secondary dark:bg-zinc-900/95">
          {/* Logo da plataforma — desktop only (no mobile o nome da mesa ja
              aparece na barra de cima). Migrou do TopNavBar para ca. */}
          <div className="hidden md:flex px-4 pt-4 pb-1">
            <Logo size="sm" />
          </div>
          <div className="p-4 md:pt-2 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-game-muted dark:text-zinc-500 mb-1">Mesa</p>
              <h3 className="font-semibold text-lg text-game-primary dark:text-zinc-100">{tableName || 'Sala de Comando'}</h3>
            </div>
            {onClose && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="md:hidden h-8 w-8 hover:bg-game-hover dark:hover:bg-white/10 rounded-full text-game-primary dark:text-white"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          
          {/* Mestre da mesa — sempre visível para todos, com presença.
              (O usuário logado não aparece aqui: o jogador já se vê pelo
              próprio personagem na lista — decisão do Paulo, 2026-07-20.) */}
          {mestreDaMesa && (
            <div className="flex items-center p-4 pt-2 pb-3">
              <div className="relative mr-3">
                <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center shadow-md">
                  <span className="text-zinc-900 text-sm font-bold">
                    {mestreDaMesa.userFullName?.charAt(0).toUpperCase() || 'M'}
                  </span>
                </div>
                <span
                  className={cn(
                    'absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-game-secondary dark:border-zinc-900',
                    isUserOnline?.(mestreDaMesa.userId) ? 'bg-emerald-500' : 'bg-zinc-500'
                  )}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="truncate text-game-primary dark:text-zinc-100 font-medium text-sm">{mestreDaMesa.userFullName}</p>
                <p className="text-game-muted dark:text-zinc-400 text-xs">
                  Mestre · {isUserOnline?.(mestreDaMesa.userId) ? 'online' : 'offline'}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="px-4 py-3 border-b border-game-border-primary dark:border-zinc-800 bg-game-secondary dark:bg-zinc-900/95">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-game-muted dark:text-zinc-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar personagens"
            className="pl-10 bg-white dark:bg-zinc-800/80 border-slate-200 dark:border-zinc-700/50 text-game-primary dark:text-zinc-100 placeholder:text-game-muted dark:placeholder:text-zinc-500 focus-visible:ring-violet-500/30"
          />
        </div>
      </div>

      {/* Criação mediada: jogadores aguardando o sorteio do mestre */}
      {sorteioMediada && (
        <SorteioPendentes
          pendentes={sorteioPendentes}
          onSortear={onSortear}
          sorteandoUserId={sorteandoUserId}
        />
      )}

      {/* Lista de Personagens - Clique abre ficha para todos */}
      <div className="flex-1 overflow-hidden">
        <CharacterList
          characters={filteredCharacters}
          membersByUserId={membersByUserId}
          disableSelection={false}
          onSelect={(char) => char && navegar('avanca', () => setEditingCharacterId(char.id))}
          onChatClick={onCharacterChatClick}
          currentUserId={currentUserId}
          unreadCounts={unreadCounts}
          isUserOnline={isUserOnline}
        />
      </div>
      
      {/* MODO SOBREPOSTO: a ficha entra por cima, num Drawer que arrasta.
          É o desktop — e continua sendo a apresentação da revelação da
          cerimônia de sorteio, montada pelo GameSession. */}
      {tableId && (
        <CharacterSheetEditor
          character={editingCharacter}
          tableId={tableId}
          open={!!editingCharacter}
          onOpenChange={(open) => !open && setEditingCharacterId(null)}
          onSave={handleSaveStats}
          isUpdating={isUpdatingStats}
          readOnly={!isMaster}
          podePedirTeste={podePedirTeste}
          convocadoOnline={convocadoOnline}
        />
      )}
    </div>
  )
}
